import _ from 'lodash'
import dayjs from 'dayjs'
import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { useTable, CellProps, Column, Cell, Row, HeaderGroup, ColumnInstance } from 'react-table'
import Box from '@mui/material/Box'
import Chip, { ChipProps } from '@mui/material/Chip'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableHead from '@mui/material/TableHead'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableCell, { TableCellProps } from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'

import CastRoundedIcon from '@mui/icons-material/CastRounded'
import LaunchIcon from '@mui/icons-material/Launch'

import CalendarIcon from '@/components/ui/icons/Calendar'
import ChipButton from '@/components/ui/buttons/ChipButton'
import Pagination from '@/components/ui/pagination/Pagination'
import colors from '@/assets/scss/_themes-vars.module.scss'

import { Consultation } from '@/core/models/consultation.model'
import { redirectTo } from '@/utils/consultations'
import { Typography } from '@mui/material'

type ConsultationsTableProps = {
  data: Consultation[]
  isDoctor?: boolean
  onRowsPerPageChange?: (rowsPerPage: number) => void
  onPageChange?: (newPage: number) => void
  rowsPerPageOptions?: number[]
  totalCount: number
}

const defaultRowsPerPageOptions = [5, 10, 15]

export default function ConsultationsTable(props: ConsultationsTableProps) {
  const { data, onPageChange, onRowsPerPageChange, rowsPerPageOptions = defaultRowsPerPageOptions, totalCount } = props
  const navigate = useNavigate()
  const { t } = useTranslation()
  const columns = useMemo(() => {
    return [
      {
        Header: t('field.name.label').toUpperCase(),
        accessor: 'patient.userInfo.surname',
        Cell: ({ value: surname }: CellProps<Consultation, string>) => (
          <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '15px' }}>
            {surname}
          </Typography>
        ),
      },
      {
        Header: t('field.firstName.label').toUpperCase(),
        accessor: 'patient.userInfo.name',
        Cell: ({ value: name }: CellProps<Consultation, string>) => (
          <Typography variant="body2" sx={{ color: colors.gray600, fontSize: '15px' }}>
            {name}
          </Typography>
        ),
      },
      {
        Header: t('field.gender.label').toUpperCase(),
        accessor: 'patient.userInfo.gender',
        Cell: ({ value: gender }: CellProps<Consultation, string>) => {
          if (!gender) return null
          let color: ChipProps['color']
          switch (gender) {
            case 'M':
              color = 'primary'
              break
            case 'F':
              color = 'secondary'
              break
            default:
              color = 'info'
              break
          }
          return <Chip color={color} label={gender} size="small" variant="filled" />
        },
      },
      {
        Header: t('page.consultations.table.header.startDate').toUpperCase(),
        accessor: 'startDate',
        Cell: ({ value }: CellProps<Consultation, Consultation['startDate']>) => (
          <Stack alignItems="center" justifyContent="start" direction="row" spacing={1}>
            <CalendarIcon fontSize="small" />
            <Chip
              color="secondary"
              label={
                <span>
                  {dayjs(value).isSame(dayjs(), 'day') ? t(`dayjs:calendar:today`) : dayjs(value).format('DD/MM/YYYY')} -{' '}
                  <b>{dayjs(value).format('HH[h]mm')}</b>
                </span>
              }
              variant="filled"
            />
          </Stack>
        ),
      },
      {
        align: 'center',
        Header: t('page.consultations.table.header.status').toUpperCase(),
        accessor: 'status',
        Cell: ({ value, row: { original: consultation } }: CellProps<Consultation, Consultation['status']>) => {
          let color: ChipProps['color']
          let status: string = value
          switch (value) {
            case 'scheduled':
              color = 'primary'
              if (!!consultation.waitingRoom) status = 'waiting'
              break
            case 'accepted':
              color = 'success'
              break
            case 'canceled':
            case 'refused':
              color = 'error'
              break
            default:
              color = 'info'
              break
          }
          return <Chip color={color} label={t(`common:model.consultation.status.${status}.label`)} size="small" variant="filled" />
        },
      },
    ] as Column<Consultation>[]
  }, [t])

  const { getTableBodyProps, headerGroups, prepareRow, rows } = useTable<Consultation>({
    columns,
    data,
  })

  const [page, setPage] = useState<number>(1)

  const handleChangePage = (newPage: number) => {
    if (page === newPage) return
    setPage(newPage)
    onPageChange?.(newPage)
  }

  const handleOpenConsultationLive = (row: Row<Consultation>) => () => {
    navigate(
      redirectTo({
        _id: row.original._id,
        status: row.original.status,
        type: row.original.type,
      })
    )
  }

  const handleOpenConsultationDetails = (row: Row<Consultation>) => () => {
    navigate(`/consultations/${row.original._id}`)
  }

  return (
    <TableContainer component={Paper} id="consultations-table">
      <Table sx={{ minWidth: 650 }} aria-label="simple table">
        <TableHead>
          {headerGroups.map((headerGroup: HeaderGroup<Consultation>) => (
            <TableRow {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column: HeaderGroup<Consultation> & { align?: TableCellProps['align'] }) => {
                return (
                  <TableCell {...column.getHeaderProps()} scope="col" align={column.align}>
                    {column.render('Header')}
                  </TableCell>
                )
              })}
              <TableCell align="right">{t('common:table.header.action').toUpperCase()}</TableCell>
            </TableRow>
          ))}
        </TableHead>
        <TableBody {...getTableBodyProps()}>
          {rows.map((row) => {
            prepareRow(row)
            return (
              <TableRow {...row.getRowProps()} key={row.original._id}>
                {row.cells.map((cell: Cell<Consultation>) => (
                  <TableCell {...cell.getCellProps()} align={(cell.column as ColumnInstance<Consultation> & { align?: TableCellProps['align'] }).align}>
                    {cell.render('Cell')}
                  </TableCell>
                ))}
                <TableCell align="right">
                  <Stack justifyContent="flex-end" direction="row" spacing={1}>
                    {_.some(['scheduled', 'active', 'accepted'], (status) => status === row.original.status) && (
                      <Tooltip title={t('page.consultations.openConsultationLiveButton.tooltip')}>
                        <ChipButton id="see" color="info" onClick={handleOpenConsultationLive(row)}>
                          <CastRoundedIcon />
                        </ChipButton>
                      </Tooltip>
                    )}
                    <Tooltip title={t('page.consultations.openConsultationDetailsButton.tooltip')}>
                      <ChipButton color="info" onClick={handleOpenConsultationDetails(row)}>
                        <LaunchIcon />
                      </ChipButton>
                    </Tooltip>
                  </Stack>
                </TableCell>
              </TableRow>
            )
          })}
        </TableBody>
      </Table>
      <Box sx={{ display: 'flex', justifyContent: 'end', p: '10px' }}>
        <Pagination
          color="primary"
          itemsPerPageOptions={rowsPerPageOptions}
          shape="rounded"
          page={page}
          onPageChange={handleChangePage}
          onItemsPerPageChange={onRowsPerPageChange}
          totalItems={totalCount}
        />
      </Box>
    </TableContainer>
  )
}
